import React, { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, Flame, CheckCircle2 } from "lucide-react";

/**
 * Monthly protocol calendar.
 * props:
 *   entries: [{date: 'YYYY-MM-DD', completed: n, total: n, pct: 0..100,
 *              checked_in: bool, fully_complete: bool}]
 *   stats?:  {days_checked_in, days_fully_complete, checkin_rate_pct,
 *             full_completion_rate_pct, current_streak}
 */
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];

export default function ProtocolCalendar({ entries, stats, compact = false }) {
  const todayISO = new Date().toISOString().slice(0, 10);
  const [cursor, setCursor] = useState(() => {
    // Default view = current month
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  // Map for O(1) lookup
  const entryByDate = useMemo(() => {
    const m = {};
    (entries || []).forEach((e) => { m[e.date] = e; });
    return m;
  }, [entries]);

  const grid = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor]);
  const monthLabel = `${MONTHS[cursor.month]} ${cursor.year}`;

  // Month-specific stats
  const monthStats = useMemo(() => {
    let done = 0, full = 0, days = 0;
    grid.forEach((row) => row.forEach((cell) => {
      if (!cell) return;
      days++;
      const e = entryByDate[cell.iso];
      if (e?.checked_in) done++;
      if (e?.fully_complete) full++;
    }));
    return { days, done, full,
             rate: days ? Math.round(done / days * 100) : 0 };
  }, [grid, entryByDate]);

  const prev = () => setCursor((c) => c.month === 0
    ? { year: c.year - 1, month: 11 }
    : { year: c.year, month: c.month - 1 });
  const next = () => {
    const now = new Date();
    if (cursor.year === now.getFullYear() && cursor.month === now.getMonth()) return;
    setCursor((c) => c.month === 11
      ? { year: c.year + 1, month: 0 }
      : { year: c.year, month: c.month + 1 });
  };

  const nextDisabled = cursor.year === new Date().getFullYear()
    && cursor.month === new Date().getMonth();

  return (
    <div className="space-y-5" data-testid="protocol-calendar">
      {/* Stat row */}
      {stats && !compact && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Current streak" value={`${stats.current_streak ?? 0}d`} icon={Flame} accent="#FF8A00" />
          <StatCard label="Days checked in" value={stats.days_checked_in} sub={`${stats.checkin_rate_pct}% rate`} />
          <StatCard label="Days fully done" value={stats.days_fully_complete} sub={`${stats.full_completion_rate_pct}% rate`} icon={CheckCircle2} accent="#E52E71" />
          <StatCard label="Last check-in" value={stats.last_checkin_day || "—"}
                    sub={stats.days_since_last_checkin != null ? `${stats.days_since_last_checkin} days ago` : "no history"} />
        </div>
      )}

      <div className="glass rounded-2xl p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF8A00]">Protocol calendar</div>
            <div className="font-display text-2xl mt-1" data-testid="calendar-month-label">{monthLabel}</div>
            <div className="text-xs text-white/45 mt-1">
              {monthStats.done}/{monthStats.days} days on · {monthStats.rate}% of the month
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={prev}
              data-testid="calendar-prev"
              className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center hover:bg-white/5 text-white/70"
              aria-label="Previous month"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={next}
              disabled={nextDisabled}
              data-testid="calendar-next"
              className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center hover:bg-white/5 text-white/70 disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Next month"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Weekday header */}
        <div className="grid grid-cols-7 gap-1.5 mb-1.5">
          {WEEKDAYS.map((w) => (
            <div key={w} className="text-[10px] tracking-[0.15em] uppercase text-white/35 text-center">
              {w}
            </div>
          ))}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7 gap-1.5">
          {grid.flat().map((cell, i) => {
            if (!cell) return <div key={i} className="aspect-square" />;
            const e = entryByDate[cell.iso];
            const pct = e?.pct || 0;
            const isToday = cell.iso === todayISO;
            const isFuture = cell.iso > todayISO;
            const intensity = Math.min(pct / 100, 1);
            // Color: full day = orange→pink gradient, partial = orange fade, none = subtle white
            let bg = "rgba(255,255,255,0.03)";
            if (e?.fully_complete) {
              bg = "linear-gradient(135deg, #FF8A00, #E52E71)";
            } else if (e?.checked_in) {
              bg = `rgba(255, 138, 0, ${intensity * 0.55 + 0.15})`;
            }
            return (
              <div
                key={i}
                data-testid={`calendar-day-${cell.iso}`}
                title={e ? `${cell.iso}: ${e.completed}/${e.total} (${e.pct}%)` : `${cell.iso}: no check-in`}
                className={`aspect-square rounded-lg relative flex flex-col items-center justify-center overflow-hidden border transition-all cursor-default ${
                  isFuture ? "opacity-30 border-white/[0.03]" : "border-white/8"
                } ${isToday ? "ring-1 ring-[#FF8A00]/70" : ""}`}
                style={{ background: bg }}
              >
                <div className={`text-xs font-medium ${
                  e?.fully_complete ? "text-white" : e?.checked_in ? "text-white/90" : "text-white/50"
                }`}>
                  {cell.day}
                </div>
                {e?.checked_in && !e.fully_complete && (
                  <div className="text-[9px] text-white/70 mt-0.5">
                    {e.completed}/{e.total}
                  </div>
                )}
                {e?.fully_complete && (
                  <CheckCircle2 size={10} className="text-white/85 mt-0.5" />
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-5 text-[10px] text-white/40 tracking-wider">
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-white/5 border border-white/10" /> None</div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded" style={{ background: "rgba(255,138,0,0.4)" }} /> Partial</div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded" style={{ background: "linear-gradient(135deg,#FF8A00,#E52E71)" }} /> Fully complete</div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded ring-1 ring-[#FF8A00]/70" /> Today</div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, accent = "#FF8A00" }) {
  return (
    <div className="glass rounded-xl p-4 border border-white/6">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.22em] uppercase text-white/40">{label}</div>
        {Icon && <Icon size={14} style={{ color: accent }} />}
      </div>
      <div className="font-display text-2xl mt-2" style={{ color: "#fff" }}>{value}</div>
      {sub && <div className="text-[11px] text-white/40 mt-1">{sub}</div>}
    </div>
  );
}

function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDow = first.getDay(); // 0=Sun
  const grid = [];
  let week = new Array(startDow).fill(null);
  for (let day = 1; day <= daysInMonth; day++) {
    const dt = new Date(year, month, day);
    const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    week.push({ day, iso });
    if (week.length === 7) {
      grid.push(week);
      week = [];
    }
  }
  if (week.length) {
    while (week.length < 7) week.push(null);
    grid.push(week);
  }
  return grid;
}
